import React from "react";
import { clsx } from "clsx";

import { CONTENT_TYPE } from "~constants";
import Headline from "~components/shared/headline/Headline";
import { type MediaConfig, Media, MediaFormat } from "~components/shared/media";
import OptionalEyebrow from "~components/shared/optional-eyebrow/OptionalEyebrow";
import { HEADLINE_SIZE } from "~types/lineup";
import { getAudio } from "~types/types";

import {
    type CardProps,
    PreviewErrorCard,
    CardSize,
    CARD_MEDIA_CONFIG,
} from "./Card";
import styles from "./Card.module.css";

export const convertMsToMinSecString = (timeInMs?: number): string => {
    if (!timeInMs) {
        return "0:00";
    }

    const timeInSeconds = timeInMs / 1000;

    const mins = Math.floor(timeInSeconds / 60);
    const secs = Math.floor(timeInSeconds % 60);

    return `${mins}:${`${secs}`.padStart(2, "0")}`;
};

export default function AudioVideoCard({
    size = CardSize.SMALL,
    item,
    className,
    storyIndex,
    linkType = "Story",
    relatedLinkType,
    relatedIndex,
}: CardProps) {
    const { headline, url, type, media } = item;
    const MEDIA_PRIORITY: MediaFormat[] = [
        MediaFormat.audioImage,
        MediaFormat.videoImage,
    ];
    const MEDIA_CONFIG: MediaConfig = {
        ...CARD_MEDIA_CONFIG,
        showPlayIcon: true,
    };

    const { audioImage, videoImage } = media || {};

    const audio = getAudio(item);

    const isVideo = type === CONTENT_TYPE.video;
    const isAudio = type === CONTENT_TYPE.audio;

    if (isVideo && !videoImage) return <PreviewErrorCard itemType="video" />;
    if (isAudio && !(audioImage && audio)) {
        return <PreviewErrorCard itemType="audio" />;
    }

    const updatedHeadline = isVideo
        ? headline.replace("(Video)", "")
        : headline.replace("(Podcast)", "");

    const durationInMilliseconds = isVideo
        ? videoImage?.videoDuration
        : parseInt(audio?.duration || "0", 10);

    const duration = convertMsToMinSecString(durationInMilliseconds);

    if (duration === "0:00") {
        return <PreviewErrorCard itemType={isVideo ? "video" : "audio"} />;
    }

    return (
        <a
            href={url}
            data-component="audio-video-card"
            data-link-type={linkType}
            data-link-index={storyIndex}
            data-link-related-type={relatedLinkType}
            data-link-related-index={relatedIndex}
            className={clsx(
                styles.card,
                styles[size],
                styles.audioVideoCard,
                className
            )}
        >
            <Media
                source={item}
                priority={MEDIA_PRIORITY}
                config={MEDIA_CONFIG}
            />
            <div className={styles.cardStoryBlock}>
                <OptionalEyebrow
                    textLabel={duration}
                    presetLabel={isVideo ? "watch" : "listen"}
                    className={styles.optionalEyebrow}
                />
                <Headline
                    text={updatedHeadline}
                    isPhoenix
                    size={HEADLINE_SIZE.EXTRA_SMALL}
                />
            </div>
        </a>
    );
}
